export type MasonryItemPosition = {
  top: number;
  left: number;
  visible: boolean;
};

/** Spalte pro Kachel. -1 = ausgeblendet (Höhe 0). */
export type MasonryColumns = number[];

/**
 * Erstes Packen: jede Kachel in die aktuell kürzeste Spalte.
 * Reihenfolge der Quelle bleibt, nur die Spalte ist höhenabhängig.
 */
export function assignMasonryColumns(heights: number[], columnCount: number): MasonryColumns {
  if (columnCount < 1) return heights.map(() => -1);
  const colHeights = Array.from({ length: columnCount }, () => 0);
  return heights.map((h) => {
    if (h <= 0) return -1;
    let target = 0;
    for (let c = 1; c < columnCount; c++) {
      if ((colHeights[c] ?? 0) < (colHeights[target] ?? 0)) target = c;
    }
    colHeights[target] = (colHeights[target] ?? 0) + h;
    return target;
  });
}

function placementBroken(
  heights: number[],
  columns: MasonryColumns,
  columnCount: number,
): boolean {
  for (let i = 0; i < heights.length; i++) {
    const visible = (heights[i] ?? 0) > 0;
    const col = columns[i] ?? -1;
    const placed = col >= 0 && col < columnCount;
    if (visible !== placed) return true;
  }
  return false;
}

/**
 * Eine Spalte ist mehr als doppelt so hoch wie die kürzeste – das passiert,
 * wenn Kacheln erst mit Platzhalterhöhe gepackt wurden und der Inhalt danach
 * in derselben Spalte gewachsen ist.
 */
export function columnsSeverelyUnbalanced(
  heights: number[],
  columns: MasonryColumns,
  columnCount: number,
): boolean {
  if (columnCount < 2) return false;
  const colHeights = Array.from({ length: columnCount }, () => 0);
  let visible = 0;
  for (let i = 0; i < heights.length; i++) {
    const h = heights[i] ?? 0;
    const col = columns[i] ?? -1;
    if (h <= 0 || col < 0 || col >= columnCount) continue;
    colHeights[col] = (colHeights[col] ?? 0) + h;
    visible += 1;
  }
  if (visible < 2) return false;
  const max = Math.max(...colHeights);
  const min = Math.min(...colHeights);
  return max > min * 2;
}

/**
 * Spaltenzuordnung beibehalten, solange dieselben Kacheln in derselben
 * Spaltenzahl stehen und das Raster noch einigermaßen gleich hoch ist.
 * Sichtbare Kacheln, die neu dazukommen oder verschwinden, und ein stark
 * ungleiches Raster werden neu auf die kürzeste Spalte gepackt.
 */
export function reconcileMasonryColumns(
  prevColumns: MasonryColumns,
  prevKeys: string[],
  prevColumnCount: number,
  keys: string[],
  heights: number[],
  columnCount: number,
): MasonryColumns {
  const structureSame =
    prevColumnCount === columnCount &&
    prevColumns.length === keys.length &&
    prevKeys.length === keys.length &&
    prevKeys.every((key, index) => key === keys[index]);

  if (
    !structureSame ||
    placementBroken(heights, prevColumns, columnCount) ||
    columnsSeverelyUnbalanced(heights, prevColumns, columnCount)
  ) {
    return assignMasonryColumns(heights, columnCount);
  }

  return prevColumns.slice();
}

export function masonryPositions(
  heights: number[],
  columns: MasonryColumns,
  columnCount: number,
  colWidth: number,
  gap: number,
): { positions: MasonryItemPosition[]; totalHeight: number } {
  if (columnCount < 1 || colWidth <= 0) {
    return {
      positions: heights.map(() => ({ top: 0, left: 0, visible: false })),
      totalHeight: 0,
    };
  }

  const colHeights = Array.from({ length: columnCount }, () => 0);
  const positions = heights.map((h, index) => {
    const col = columns[index] ?? -1;
    if (h <= 0 || col < 0 || col >= columnCount) {
      return { top: 0, left: 0, visible: false };
    }
    const top = colHeights[col] ?? 0;
    colHeights[col] = top + h + gap;
    return { top, left: col * (colWidth + gap), visible: true };
  });

  const totalHeight = Math.max(0, ...colHeights.map((h) => (h > 0 ? h - gap : 0)));
  return { positions, totalHeight };
}
